import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  useStreams,
  useStreamForEdit,
  useStreamNewOptions,
  useCustomerOrganisations,
  useCreateStream,
  useUpdateStream,
  useDeleteStream,
  streamKeys,
} from '../hooks';
import { StreamsTable } from '../components/StreamsTable';
import { StreamFormModal, StreamDeleteDialog } from '../components/modals';
import type { Stream, StreamsFilters, StreamFormData } from '../types';

// =============================================================================
// Column filter persistence
// =============================================================================

const COLUMN_FILTERS_STORAGE_KEY = 'atek-streams-column-filters';

function loadColumnFilters(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(COLUMN_FILTERS_STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
}

// =============================================================================
// Constants
// =============================================================================

const PAGE_SIZE = 25;

// Columns whose filters are handled server-side via searchable-select (skip in client filter)
const SERVER_SIDE_COLUMNS = new Set(['organisationName']);

// Date-range filter columns
const DATE_FILTER_COLUMNS = new Set(['calibrationDue', 'updatedAt', 'createdAt']);

// Column key → Stream field mapping for client-side filtering
const COLUMN_FIELD_MAP: Record<string, keyof Stream> = {
  name: 'name',
  sensorName: 'sensorName',
  probeSerial: 'probeSerial',
  transmitterName: 'transmitterName',
  calibrationCertNumber: 'calibrationCertNumber',
  calibrationDue: 'calibrationDue',
  updatedAt: 'updatedAt',
  createdAt: 'createdAt',
  organisationName: 'organisationName',
};

// =============================================================================
// StreamsPage
// =============================================================================

export default function StreamsPage() {
  const { t } = useTranslation('streams');
  const queryClient = useQueryClient();

  // Server-side state
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [sortKey, setSortKey] = useState<StreamsFilters['sortKey']>('updatedAt');
  const [sortDirection, setSortDirection] = useState<1 | -1>(-1);

  // Column filters (persisted to localStorage)
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(loadColumnFilters);

  useEffect(() => {
    localStorage.setItem(COLUMN_FILTERS_STORAGE_KEY, JSON.stringify(columnFilters));
  }, [columnFilters]);

  const setColumnFilter = (key: string, value: string) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
  };

  // Debounce toolbar search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Extract text column filter values for server-side search promotion
  const columnSearchText = useMemo(() => {
    return Object.entries(columnFilters)
      .filter(([, value]) => {
        if (!value) return false;
        if (value.includes('>>')) return false; // date-range
        if (value.startsWith('[')) return false; // multi-select / searchable-select
        return true;
      })
      .map(([, value]) => value)
      .join(' ');
  }, [columnFilters]);

  // Debounce column search text for server-side queries
  const [debouncedColumnSearch, setDebouncedColumnSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedColumnSearch(columnSearchText);
      if (columnSearchText) setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [columnSearchText]);

  // Combine toolbar search + column filter text into one server-side search param
  const combinedSearch = [debouncedSearch, debouncedColumnSearch].filter(Boolean).join(' ') || undefined;

  // Modal state
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingStreamId, setEditingStreamId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [deletingStream, setDeletingStream] = useState<Stream | null>(null);

  const filters: StreamsFilters = {
    search: combinedSearch,
    sortKey,
    sortDirection,
    pageNumber: page,
    pageLength: PAGE_SIZE,
  };

  // Queries
  const streamsQuery = useStreams(filters);
  const editQuery = useStreamForEdit(editingStreamId);
  const newOptionsQuery = useStreamNewOptions(isCreating && isFormOpen);
  const customersQuery = useCustomerOrganisations();

  // Mutations
  const createMutation = useCreateStream();
  const updateMutation = useUpdateStream();
  const deleteMutation = useDeleteStream();

  const rawStreams = streamsQuery.data?.streams || [];
  const rawTotal = streamsQuery.data?.total || 0;

  // ---------------------------------------------------------------------------
  // Client-side column filtering (date-range + searchable-select within page)
  // ---------------------------------------------------------------------------
  const filteredStreams = useMemo(() => {
    let result = rawStreams;
    const activeFilters = Object.entries(columnFilters).filter(([, v]) => v);
    if (activeFilters.length === 0) return result;

    for (const [colKey, value] of activeFilters) {
      // Text filters are promoted to server-side — skip in client filter
      if (!DATE_FILTER_COLUMNS.has(colKey) && !SERVER_SIDE_COLUMNS.has(colKey)) continue;

      const field = COLUMN_FIELD_MAP[colKey];
      if (!field) continue;

      if (DATE_FILTER_COLUMNS.has(colKey)) {
        const [from, to] = value.split('>>');
        if (from) result = result.filter((s) => String(s[field] || '') >= from);
        if (to) result = result.filter((s) => String(s[field] || '') <= to + 'T23:59:59');
        continue;
      }

      if (SERVER_SIDE_COLUMNS.has(colKey)) {
        // searchable-select: JSON array of selected values
        try {
          const selected = JSON.parse(value) as string[];
          if (selected.length) result = result.filter((s) => selected.includes(String(s[field] || '')));
        } catch { /* ignore */ }
        continue;
      }
    }

    return result;
  }, [rawStreams, columnFilters]);

  const streams = filteredStreams;
  const total = rawTotal;

  // ---------------------------------------------------------------------------
  // Searchable-select options for organisation column
  // ---------------------------------------------------------------------------
  const customers = customersQuery.data || [];
  const searchableSelectOptions = useMemo(() => ({
    organisationName: customers
      .map((c) => ({ label: c.name, value: c.name }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  }), [customers]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------
  const handleSort = (key: StreamsFilters['sortKey']) => {
    if (key === sortKey) {
      setSortDirection(sortDirection === 1 ? -1 : 1);
    } else {
      setSortKey(key);
      setSortDirection(-1);
    }
    setPage(0);
  };

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: streamKeys.lists() });
  };

  const handleCreateClick = () => {
    setEditingStreamId(null);
    setIsCreating(true);
    setIsFormOpen(true);
  };

  const handleEditClick = (stream: Stream) => {
    setEditingStreamId(stream.mongoStreamId);
    setIsCreating(false);
    setIsFormOpen(true);
  };

  const handleDeleteClick = (stream: Stream) => {
    setDeletingStream(stream);
  };

  const handleFormSubmit = async (data: StreamFormData) => {
    if (editingStreamId) {
      await updateMutation.mutateAsync({ id: editingStreamId, data });
    } else {
      await createMutation.mutateAsync(data);
    }
    setIsFormOpen(false);
    setEditingStreamId(null);
    setIsCreating(false);
  };

  const handleDeleteConfirm = () => {
    if (!deletingStream) return;
    deleteMutation.mutate(deletingStream.mongoStreamId, {
      onSuccess: () => setDeletingStream(null),
    });
  };

  const modalOptions = editingStreamId
    ? editQuery.data?.options || null
    : newOptionsQuery.data || null;

  const modalStream = editingStreamId ? editQuery.data?.stream || null : null;

  const isModalReady = editingStreamId
    ? !editQuery.isLoading
    : !isCreating || !newOptionsQuery.isLoading;

  return (
    <div className="space-y-5">
      <nav className="breadcrumb flex items-center gap-1.5">
        <Link to="/operations/dashboard" className="text-atek-blue hover:text-atek-blue-dark">
          {t('page.operations')}
        </Link>
        <span className="text-muted-foreground">/</span>
        <span className="text-foreground">{t('page.title')}</span>
      </nav>

      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-lg font-semibold text-foreground">{t('page.title')}</h1>
          <p className="text-[13px] text-muted-foreground mt-1">{t('page.subtitle')}</p>
        </div>
        <Button onClick={handleCreateClick}>
          <Plus className="h-4 w-4 mr-1" />
          {t('actions.create')}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t('page.allStreams')}</CardTitle>
              <CardDescription>
                {t('page.streamsFound', { count: total })}
              </CardDescription>
            </div>
            <Input
              placeholder={t('page.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-64"
            />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {streamsQuery.error ? (
            <div className="py-8 text-center">
              <p className="text-destructive">{t('errors.loadStreams')}</p>
            </div>
          ) : (
            <StreamsTable
              streams={streams}
              pageItemCount={rawStreams.length}
              total={total}
              sortKey={sortKey}
              sortDirection={sortDirection}
              onSort={handleSort}
              onEdit={handleEditClick}
              onDelete={handleDeleteClick}
              page={page}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
              columnFilters={columnFilters}
              onColumnFilterChange={setColumnFilter}
              onClearColumnFilters={() => setColumnFilters({})}
              isLoading={streamsQuery.isLoading}
              onRefresh={handleRefresh}
              isRefreshing={streamsQuery.isFetching}
              searchableSelectOptions={searchableSelectOptions}
            />
          )}
        </CardContent>
      </Card>

      <StreamFormModal
        open={isFormOpen && isModalReady}
        onOpenChange={(open) => {
          if (!open) {
            setIsFormOpen(false);
            setEditingStreamId(null);
            setIsCreating(false);
          }
        }}
        stream={modalStream}
        options={modalOptions}
        onSubmit={handleFormSubmit}
        isLoading={createMutation.isPending || updateMutation.isPending}
      />

      <StreamDeleteDialog
        open={!!deletingStream}
        onOpenChange={(open) => {
          if (!open) setDeletingStream(null);
        }}
        stream={deletingStream}
        onConfirm={handleDeleteConfirm}
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
